import clsx from 'clsx';
import { PiggyBank } from 'lucide-react';
import { useState } from 'react';
import { formatRupees } from '../lib/money';
import { useMyMaintenance, useSpending } from '../lib/queries';
import { Card, Empty, ErrorNote, Field, Input, PageHeader, Segmented, Spinner } from '../ui';

const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const fyStartMonth = (d: string) => {
  const y = Number(d.slice(0, 4));
  return `${Number(d.slice(5, 7)) >= 4 ? y : y - 1}-04`;
};
const monthName = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

type View = 'total' | 'sqft';

/** Where the maintenance money went, by head and by month. Shown to the committee and to residents. */
export function SpendingView({ myAreaSqft = 0 }: { myAreaSqft?: number }) {
  const [from, setFrom] = useState(fyStartMonth(todayIST()));
  const [to, setTo] = useState(todayIST().slice(0, 7));
  const [view, setView] = useState<View>(myAreaSqft > 0 ? 'sqft' : 'total');
  const q = useSpending(from, to);
  const d = q.data;
  const perSqft = view === 'sqft' && !!d && d.totalAreaSqft > 0;
  // Per sq ft = society total ÷ total area of all flats. Money stays in paise; only the display divides.
  const show = (paise: number) => (perSqft ? `${formatRupees(paise / d!.totalAreaSqft)}/sq ft` : formatRupees(paise, { hideZeroFraction: true }));
  const maxHead = Math.max(1, ...(d?.heads.map((h) => h.spentPaise) ?? [1]));

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="From month"><Input type="month" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></Field>
          <Field label="To month"><Input type="month" value={to} min={from} max={todayIST().slice(0, 7)} onChange={(e) => e.target.value && setTo(e.target.value)} /></Field>
        </div>
        <Segmented<View>
          value={perSqft ? 'sqft' : 'total'}
          onChange={setView}
          options={[{ value: 'total', label: 'Total ₹' }, { value: 'sqft', label: 'Per sq ft' }]}
        />
        {perSqft && d && d.unitsMissingArea > 0 && <p className="text-xs text-amber-800">{d.unitsMissingArea} flat{d.unitsMissingArea > 1 ? 's have' : ' has'} no area entered yet, so per sq ft figures are slightly high.</p>}
        {view === 'sqft' && d && d.totalAreaSqft === 0 && <p className="text-sm text-amber-800">Flat areas are not entered yet, so per sq ft cannot be shown.</p>}
      </Card>

      {q.isLoading ? <Spinner /> : q.error || !d ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Card className="!p-3"><div className="text-xs text-slate-500">Collected</div><div className="font-bold tabular-nums text-emerald-800">{show(d.collectedPaise)}</div></Card>
            <Card className="!p-3"><div className="text-xs text-slate-500">Spent</div><div className="font-bold tabular-nums text-slate-900">{show(d.spentPaise)}</div></Card>
            <Card className={clsx('!p-3', d.closingPaise < 0 && 'bg-rose-50')}>
              <div className="text-xs text-slate-500">{d.closingPaise < 0 ? 'Carried forward (short)' : 'Carried forward'}</div>
              <div className={clsx('font-bold tabular-nums', d.closingPaise < 0 ? 'text-rose-800' : 'text-slate-900')}>{show(d.closingPaise)}</div>
            </Card>
          </div>
          {perSqft && myAreaSqft > 0 && (
            <Card className="bg-brand-50 !p-3 text-sm text-brand-900">
              Your flat ({myAreaSqft.toLocaleString('en-IN')} sq ft): maintenance spent on your share is <b>{formatRupees((d.spentPaise / d.totalAreaSqft) * myAreaSqft, { hideZeroFraction: true })}</b>, against <b>{formatRupees((d.collectedPaise / d.totalAreaSqft) * myAreaSqft, { hideZeroFraction: true })}</b> collected.
            </Card>
          )}
          {d.openingPaise !== 0 && <p className="text-center text-xs text-slate-500">Brought in from before {monthName(from)}: {show(d.openingPaise)}</p>}

          <Card className="space-y-3">
            <h2 className="font-semibold">Spent on</h2>
            {d.heads.length === 0 ? <p className="text-sm text-slate-500">No spending recorded in these months.</p> : d.heads.map((h) => (
              <div key={h.accountId}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span>{h.name} <span className="text-xs text-slate-500">{d.spentPaise > 0 ? `${Math.round((h.spentPaise / d.spentPaise) * 100)}%` : ''}</span></span>
                  <span className="font-semibold tabular-nums">{show(h.spentPaise)}</span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-brand-600" style={{ width: `${Math.max(2, (Math.max(0, h.spentPaise) / maxHead) * 100)}%` }} /></div>
              </div>
            ))}
          </Card>

          <Card className="space-y-2">
            <h2 className="font-semibold">Month by month</h2>
            <p className="text-xs text-slate-500">Whatever is left in a month moves to the next; if more was spent than collected, the shortfall moves forward too.</p>
            {d.months.length === 0 ? <Empty icon={<PiggyBank className="h-8 w-8" />} title="Nothing to show" /> : (
              <div className="divide-y divide-slate-100">
                {d.months.map((m) => (
                  <details key={m.month} className="py-2">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm">
                      <span className="font-semibold">{monthName(m.month)}</span>
                      <span className="flex gap-3 tabular-nums"><span className="text-emerald-800">+{show(m.collectedPaise)}</span><span>−{show(m.spentPaise)}</span><span className={clsx('font-semibold', m.closingPaise < 0 && 'text-rose-800')}>{show(m.closingPaise)}</span></span>
                    </summary>
                    <div className="mt-2 space-y-1 rounded-xl bg-slate-50 p-3 text-sm">
                      <div className="flex justify-between"><span>Brought forward</span><span className="tabular-nums">{show(m.openingPaise)}</span></div>
                      <div className="flex justify-between"><span>Collected</span><span className="tabular-nums">{show(m.collectedPaise)}</span></div>
                      {m.byHead.map((b) => (
                        <div key={b.accountId} className="flex justify-between text-slate-700"><span>− {d.heads.find((h) => h.accountId === b.accountId)?.name}</span><span className="tabular-nums">{show(b.spentPaise)}</span></div>
                      ))}
                      <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold"><span>Carried forward</span><span className="tabular-nums">{show(m.closingPaise)}</span></div>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export function SpendingScreen() {
  return (
    <div className="space-y-4">
      <PageHeader title="Where maintenance goes" />
      <SpendingView />
    </div>
  );
}
